import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { EmailType } from "../../Enums/EmailEnum";
import ToogleSwitch from "../../components/staff/ToogleSwitch";
import LegacyButton from "../../components/Atomos/Buttons/LegacyButton";
import Button from "../../components/staff/Button";
import SortDefault from "../../components/Atomos/Icons/SortDefault";
import chevronRightButton from "../../assets/Fonts/chevron-right-button.svg";
import { toastError, toastSuccess } from "../../utils/toast";
import {
  getMyEmailSubscriptions,
  putMyEmailSubscriptions,
  getEmailSubscriptionsByPersonId,
  putEmailSubscriptionsByPersonId,
  MyEmailSubscriptionsResponse,
} from "../../Services/EmailUnsubscriber";
import {
  EmailTypeKey,
  GDPR_KEY,
  EMAIL_TYPE_ENTRIES,
  GDPR_FOOTNOTE,
  individualSubsFromUnsubscribed,
  displaySubsFromIndividual,
  unsubscribedToSave,
  unsubscribedListsEqual,
} from "./emailSubscriptionHelpers";

type SortOrder = "asc" | "desc" | null;

export type EmailSubscriptionsTabVariant = "member" | "staff";

export interface EmailSubscriptionsTabProps {
  variant?: EmailSubscriptionsTabVariant;
  personId?: string;
  /** Called after successful load with the API response (staff pages use this for breadcrumb). */
  onLoaded?: (data: MyEmailSubscriptionsResponse | null) => void;
  /** Staff only: shown as Cancel next to Save (typically navigate back). */
  onCancel?: () => void;
}

export default function EmailSubscriptionsTab({
  variant = "member",
  personId,
  onLoaded,
  onCancel,
}: EmailSubscriptionsTabProps): React.ReactElement {
  const isStaff = variant === "staff";

  const [individualSubs, setIndividualSubs] = useState<
    Record<EmailTypeKey, boolean>
  >(() => individualSubsFromUnsubscribed([]));
  const [savedUnsubscribed, setSavedUnsubscribed] = useState<EmailType[]>([]);
  const [sortOrder, setSortOrder] = useState<SortOrder>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const displaySubs = useMemo(
    () => displaySubsFromIndividual(individualSubs),
    [individualSubs],
  );

  const isGdprSubscribed = individualSubs[GDPR_KEY];

  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        let data: MyEmailSubscriptionsResponse | null = null;
        if (isStaff) {
          if (!personId) {
            throw new Error("personId is required for staff variant");
          }
          data = await getEmailSubscriptionsByPersonId(personId);
        } else {
          data = await getMyEmailSubscriptions();
        }
        if (cancelled) {
          return;
        }
        const unsubscribed = data?.unsubscribedCategories ?? [];
        const nextIndividual = individualSubsFromUnsubscribed(unsubscribed);
        setIndividualSubs(nextIndividual);
        setSavedUnsubscribed(unsubscribedToSave(nextIndividual));
        onLoadedRef.current?.(data);
      } catch {
        if (!cancelled) {
          toastError("Could not load email subscriptions.");
          onLoadedRef.current?.(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isStaff, personId]);

  const sortedRows = useMemo(() => {
    const rows = [...EMAIL_TYPE_ENTRIES];
    if (!sortOrder) {
      return rows;
    }
    rows.sort(([, labelA], [, labelB]) => {
      const cmp = labelA.localeCompare(labelB);
      return sortOrder === "asc" ? cmp : -cmp;
    });
    return rows;
  }, [sortOrder]);

  const hasChanges = !unsubscribedListsEqual(
    unsubscribedToSave(individualSubs),
    savedUnsubscribed,
  );

  const handleToggle = useCallback((key: EmailTypeKey, checked: boolean) => {
    setIndividualSubs((prev) => ({ ...prev, [key]: checked }));
  }, []);

  const handleSortClick = useCallback(() => {
    setSortOrder((prev) => {
      if (prev === null) {
        return "asc";
      }
      if (prev === "asc") {
        return "desc";
      }
      return null;
    });
  }, []);

  const handleSave = useCallback(async () => {
    if (!hasChanges || saving) {
      return;
    }
    setSaving(true);
    try {
      const unsubscribedCategories = unsubscribedToSave(individualSubs);
      if (isStaff) {
        if (!personId) {
          throw new Error("personId is required");
        }
        await putEmailSubscriptionsByPersonId(personId, unsubscribedCategories);
      } else {
        await putMyEmailSubscriptions(unsubscribedCategories);
      }
      setSavedUnsubscribed(unsubscribedCategories);
      toastSuccess("Email subscriptions saved.");
    } catch {
      toastError("Could not save email subscriptions.");
    } finally {
      setSaving(false);
    }
  }, [hasChanges, saving, individualSubs, isStaff, personId]);

  const sortActiveClass = isStaff ? "text-secondary" : "text-primary";
  const saveDisabled = !hasChanges || saving || loading;

  return (
    <div className="w-full flex flex-col gap-6">
      <div className="w-full overflow-x-auto border border-gray-200 rounded">
        <table className="w-full border-collapse text-left">
          <thead>
            <tr className="bg-gray-100 text-gray-font text-sm font-semibold">
              <th className="px-4 py-3 w-12">#</th>
              <th className="px-4 py-3">
                <button
                  type="button"
                  onClick={handleSortClick}
                  className="inline-flex items-center gap-2 cursor-pointer select-none"
                >
                  <span>Email Type</span>
                  <span
                    className={`w-4 h-4 ${
                      sortOrder ? sortActiveClass : "text-gray-400"
                    }`}
                  >
                    <SortDefault />
                  </span>
                </button>
              </th>
              <th className="px-4 py-3 w-32 text-center">Subscribe</th>
            </tr>
          </thead>
          <tbody>
            {sortedRows.map(([key, label], index) => (
              <tr
                key={key}
                className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
              >
                <td className="px-4 py-3 text-sm text-gray-font border-t border-gray-100">
                  {index + 1}
                </td>
                <td className="px-4 py-3 text-sm text-gray-font border-t border-gray-100">
                  {key === GDPR_KEY ? `${label}*` : label}
                </td>
                <td className="px-4 py-3 border-t border-gray-100">
                  <div className="flex justify-center">
                    <ToogleSwitch
                      checked={displaySubs[key]}
                      onChange={(checked) => handleToggle(key, checked)}
                      disabled={
                        loading ||
                        saving ||
                        (!isGdprSubscribed && key !== GDPR_KEY)
                      }
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-sm text-gray-font text-left">*{GDPR_FOOTNOTE}</p>

      <div className="flex justify-end gap-3">
        {isStaff ? (
          <>
            {onCancel ? (
              <Button
                text="Cancel"
                onClick={onCancel}
                disabled={saving}
                color="neutral"
                variant="outlined"
                size="fixed"
              />
            ) : null}
            <Button
              text={saving ? "Saving…" : "Save"}
              onClick={handleSave}
              disabled={saveDisabled}
              color="primary"
              variant="filled"
              size="fixed"
              className={saveDisabled ? "!opacity-50" : ""}
            />
          </>
        ) : (
          <LegacyButton
            text="Save"
            onClick={handleSave}
            disabled={saveDisabled}
            content="textIcon"
            className={`${
              hasChanges && !saving && !loading
                ? "!opacity-100"
                : "!opacity-50"
            } w-auto min-w-[8rem]`}
            icon={
              <img
                src={chevronRightButton}
                alt="chevron"
                className="w-3 h-3"
              />
            }
          />
        )}
      </div>
    </div>
  );
}
